import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/icons';
import { MOCK_MODE } from '../lib/api';
import { Badge, EmptyState, ErrorState, KeyHint, LoadingRows, Segmented, ToneDot } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../lib/errors';
import { formatDayLabel, formatPhone, maskPhone, relativeTime } from '../lib/format';
import { useHotkeys } from '../lib/hotkeys';
import { useConversation, useInbox } from '../lib/queries';
import type { Conversation, InboxFilter } from '../lib/types';

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: 'all', label: 'Tümü' },
  { value: 'mine', label: 'Atanmışım' },
  { value: 'waiting', label: 'Yanıt bekleyen' },
  { value: 'ai', label: 'AI ticket' },
  { value: 'test', label: 'Test' }
];

const LABELS = ['kargo', 'iade', 'teklif', 'stok', 'fatura', 'ankaraqr'];

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** S3 — Gelen kutusu: filtreler | liste | seçili konuşma önizlemesi (232 | 1fr | 320). */
export default function InboxPage() {
  const { id: selectedId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const searchRef = useRef<HTMLInputElement>(null);

  const inbox = useInbox({ filter, search: debouncedSearch, userId: user?.id ?? null });
  const all = useInbox({ filter: 'all', search: '', userId: user?.id ?? null });
  const selected = useConversation(selectedId);

  const conversations = inbox.data?.items ?? [];
  const counts = useMemo(() => {
    const items = all.data?.items ?? [];
    return {
      all: items.length,
      mine: items.filter((item) => item.assignedTo === user?.id).length,
      waiting: items.filter((item) => item.status !== 'closed' && item.waitingSince !== null).length,
      ai: items.filter((item) => item.aiState === 'ai').length,
      test: items.filter((item) => item.isTest).length
    } as Record<InboxFilter, number>;
  }, [all.data]);

  const labelCounts = useMemo(() => {
    const items = all.data?.items ?? [];
    return LABELS.map((label) => ({ label, count: items.filter((item) => item.labels.includes(label)).length })).filter(
      (entry) => entry.count > 0
    );
  }, [all.data]);

  const selectedIndex = conversations.findIndex((item) => item.id === selectedId);

  function move(delta: number) {
    if (conversations.length === 0) return;
    const nextIndex = selectedIndex < 0 ? 0 : Math.min(conversations.length - 1, Math.max(0, selectedIndex + delta));
    const next = conversations[nextIndex];
    if (next) navigate('/inbox/' + next.id);
  }

  useHotkeys([
    { key: 'j', handler: () => move(1) },
    { key: 'k', handler: () => move(-1) },
    {
      key: 'Enter',
      handler: () => {
        if (selectedId) navigate('/konusma/' + selectedId);
        else if (conversations[0]) navigate('/konusma/' + conversations[0].id);
      }
    },
    { key: '/', handler: () => searchRef.current?.focus() },
    { key: 'Escape', handler: () => setSearch('') }
  ]);

  return (
    <div className="inbox">
      {/* Filtreler */}
      <aside className="pane inbox-filters" aria-label="Filtreler">
        <div className="border-b border-line px-3 py-2">
          <div className="eyebrow">Gelen Kutusu</div>
          <div className="mt-1 text-[11px] text-muted">Kuyruk ve etiketler</div>
        </div>
        <nav className="px-2 py-2">
          <ul>
            {FILTERS.map((entry) => {
              const active = entry.value === filter;
              return (
                <li key={entry.value}>
                  <button
                    type="button"
                    aria-pressed={active}
                    className={
                      'flex h-8 w-full items-center gap-2 rounded-[4px] px-2 text-left text-[13px] ' +
                      (active ? 'bg-signal-soft font-medium text-signal' : 'text-ink-2 hover:bg-surface-2')
                    }
                    onClick={() => setFilter(entry.value)}
                  >
                    <span className="flex-1 truncate">{entry.label}</span>
                    <span className="mono text-[11px] text-muted">{counts[entry.value] ?? 0}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Etiketler</div>
          <ul>
            {labelCounts.length === 0 ? (
              <li className="px-2 py-1 text-[12px] text-muted">Etiket yok</li>
            ) : (
              labelCounts.map((entry) => (
                <li key={entry.label}>
                  <button
                    type="button"
                    className="flex h-7 w-full items-center gap-2 rounded-[4px] px-2 text-left text-[12px] text-ink-2 hover:bg-surface-2"
                    onClick={() => setSearch(entry.label)}
                  >
                    <span className="flex-1 truncate">{entry.label}</span>
                    <span className="mono text-[11px] text-muted">{entry.count}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </nav>
        <div className="mt-auto border-t border-line px-3 py-2 text-[11px] text-muted">
          <div className="flex items-center gap-2">
            <KeyHint keys={['J']} />
            <KeyHint keys={['K']} />
            <span>gezin</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <KeyHint keys={['Enter']} />
            <span>konuşmayı aç</span>
          </div>
        </div>
      </aside>

      {/* Liste */}
      <section className="pane" aria-label="Konuşma listesi">
        <div className="flex items-center gap-2 border-b border-line px-3 py-2">
          <div className="relative flex-1">
            <Icon name="search" size={14} className="pointer-events-none absolute left-2 top-2 text-muted" />
            <input
              ref={searchRef}
              className="input pl-7"
              placeholder="Ad, numara veya mesaj ara"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Konuşma ara"
            />
          </div>
          <div className="hidden min-[1360px]:block">
            <Segmented value={filter} options={FILTERS} onChange={setFilter} label="Hızlı filtre" />
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-line px-3 py-1 text-[11px] text-muted">
          <span>
            {inbox.isLoading ? 'Yükleniyor…' : conversations.length + ' konuşma'}
            {debouncedSearch ? ' · "' + debouncedSearch + '"' : ''}
          </span>
          <span className="flex items-center gap-2">
            <ToneDot tone={MOCK_MODE ? 'muted' : 'ok'} /> {MOCK_MODE ? 'örnek veri' : 'canlı'}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {inbox.isError ? (
            <ErrorState message={errorMessage(inbox.error)} onRetry={() => void inbox.refetch()} />
          ) : inbox.isLoading ? (
            <LoadingRows rows={8} columns={3} />
          ) : conversations.length === 0 ? (
            <EmptyState
              icon="inbox"
              title="Konuşma bulunamadı"
              hint={debouncedSearch ? 'Arama ölçütlerini değiştirin.' : 'Bu filtrede bekleyen konuşma yok.'}
            />
          ) : (
            <ul>
              {conversations.map((conversation) => (
                <li key={conversation.id}>
                  <ConversationRow conversation={conversation} active={conversation.id === selectedId} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Seçili önizleme */}
      <aside className="pane inbox-rail" aria-label="Seçili konuşma">
        {!selectedId ? (
          <EmptyState
            icon="inbox"
            title="Konuşma seçin"
            hint="Listeden bir satır seçin; burada son mesajlar ve kişi özeti görünür. Enter ile konuşmayı açabilirsiniz."
          />
        ) : selected.isLoading ? (
          <LoadingRows rows={6} columns={2} />
        ) : selected.isError ? (
          <ErrorState message={errorMessage(selected.error)} onRetry={() => void selected.refetch()} />
        ) : selected.data ? (
          <div className="flex h-full flex-col">
            <div className="border-b border-line px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-medium">
                    {selected.data.contact.name || maskPhone(selected.data.contact.phone)}
                  </div>
                  <div className="mono mt-0.5 text-[12px] text-muted">{formatPhone(selected.data.contact.phone)}</div>
                </div>
                <Link
                  to={'/konusma/' + selected.data.conversation.id}
                  className="btn btn-sm btn-primary shrink-0"
                  aria-label="Konuşmayı aç"
                >
                  Aç
                </Link>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {selected.data.conversation.isTest ? <Badge tone="warn">Test</Badge> : null}
                <Badge tone={selected.data.conversation.aiState === 'ai' ? 'signal' : 'neutral'}>
                  {selected.data.conversation.aiState === 'ai' ? 'AI devrede' : 'Ajan'}
                </Badge>
                {selected.data.contact.optIn ? <Badge tone="ok">Opt-in</Badge> : <Badge tone="danger">Opt-in yok</Badge>}
                {selected.data.conversation.labels.map((label) => (
                  <Badge key={label}>{label}</Badge>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="space-y-2">
                {selected.data.messages.slice(-5).map((message) => (
                  <div key={message.id} className="text-[12px]">
                    <div className="flex items-center gap-2 text-[11px] text-muted">
                      <span className="font-medium text-ink-2">
                        {message.author === 'customer' ? 'Müşteri' : message.author === 'ai' ? 'AI' : message.author === 'agent' ? 'Ajan' : 'Sistem'}
                      </span>
                      <span>{relativeTime(message.at)}</span>
                      {message.isNote ? <Badge tone="warn">Not</Badge> : null}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-ink-2">{message.body}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-line px-4 py-3">
              <div className="eyebrow mb-1">Kişi</div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-muted">Oluşturulma</span>
                <span className="mono">{formatDayLabel(selected.data.contact.createdAt)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[12px]">
                <span className="text-muted">Sipariş</span>
                <span className="mono">{selected.data.contact.orders.length}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-[12px]">
                <span className="text-muted">Ticket</span>
                <span className="mono">{selected.data.contact.ticketCount}</span>
              </div>
              <Link to={'/kisi/' + selected.data.contact.id} className="btn btn-sm mt-3 w-full">
                <Icon name="user" size={14} />
                Kişi 360
              </Link>
            </div>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function ConversationRow({ conversation, active }: { conversation: Conversation; active: boolean }) {
  const navigate = useNavigate();
  const name = conversation.name || maskPhone(conversation.phone);
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      className="row h-auto items-start py-2"
      onClick={() => navigate('/inbox/' + conversation.id)}
      onDoubleClick={() => navigate('/konusma/' + conversation.id)}
    >
      <span className="mt-1 flex w-3 shrink-0 justify-center">
        {conversation.unread > 0 ? <ToneDot tone="danger" /> : <ToneDot tone="muted" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={'truncate text-[13px] ' + (conversation.unread > 0 ? 'font-semibold text-ink' : 'font-medium text-ink-2')}>
            {name}
          </span>
          {conversation.isTest ? <Badge tone="warn">Test</Badge> : null}
          {conversation.aiState === 'ai' ? <Badge tone="signal">AI</Badge> : null}
          <span className="mono ml-auto shrink-0 text-[11px] text-muted">{relativeTime(conversation.lastAt)}</span>
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span className="truncate text-[12px] text-muted">{conversation.lastMessage}</span>
          {conversation.unread > 0 ? (
            <span className="mono ml-auto shrink-0 rounded-[4px] bg-signal px-1 text-[10px] text-white">{conversation.unread}</span>
          ) : null}
        </span>
      </span>
    </button>
  );
}
